/**
 * @file: ProductionTracker.jsx
 * @status: Sovereign Factory Intelligence (V12.8.5) - ULTIMATE SYNC & UI FIX
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: إصلاح تزامن التدشين؛ تحديث Progress لـ AntD الجديد؛ فرض التعتيم البصري؛ الحفاظ على بروتوكول @CEO.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Factory, Zap, ShieldCheck, Plus, ArrowLeft, Activity, 
  Timer, Loader2, Gauge, AlertCircle, Lock,
  Box, Cpu, Layers, Workflow, Fingerprint,
  Monitor, HardDrive, Globe, Trash2, Edit3, Search, LayoutGrid, BarChart3
} from 'lucide-react';
import { message, Spin, Tag, Tooltip, Progress, Badge, Modal, Input, Empty, Button } from 'antd';

import logo2026 from '../assets/logo2026.png'; 

const ProductionTracker = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [productionLines, setProductionLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [lineStages, setLineStages] = useState([]); 
  const [isApproving, setIsApproving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStages, setLoadingStages] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const userRole = localStorage.getItem('huger_user_role') || 'CEO';
  const API_URL = "https://portal.huger.sa/api.php";

  // --- [ 📡 محرك الربط السيادي - جلب البيانات ] ---
  const fetchProductionLines = useCallback(async () => {
    try {
      const res = await axios.post(API_URL, 
        { action: 'get_production_lines' },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (Array.isArray(res.data)) {
        setProductionLines(res.data);
      }
    } catch (err) {
      console.error("Critical Sync Error", err);
      message.error(i18n.language === 'ar' ? "عذراً رئيس، تعذر الاتصال بنظام المصنع." : "Factory sync failed.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, i18n.language]);

  useEffect(() => {
    fetchProductionLines();
    const interval = setInterval(fetchProductionLines, 15000); // تحديث كل 15 ثانية لضمان الرصد
    return () => clearInterval(interval);
  }, [fetchProductionLines]);

  const fetchLineStages = async (lineId) => {
    setLoadingStages(true);
    try {
      const res = await axios.post(API_URL, 
        { action: 'get_line_stages', line_id: lineId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (Array.isArray(res.data)) {
        setLineStages(res.data);
      }
    } catch (err) {
      message.error("Telemetry failure.");
      setLineStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  useEffect(() => {
    if (selectedLine) fetchLineStages(selectedLine.id);
  }, [selectedLine]);

  // --- [ 🚀 بروتوكول التدشين الفوري المحدث - الإصلاح الجذري ] ---
  const handleCreateNewLine = async () => {
    const hide = message.loading(i18n.language === 'ar' ? "جاري تعميد تدشين خط إنتاج جديد..." : "Launching line...", 0);
    try {
      const response = await axios.post(API_URL, {
        action: 'create_production_line',
        name: i18n.language === 'ar' ? `خط إنتاج ${new Date().toLocaleTimeString('ar-SA')}` : `Line ${new Date().toLocaleTimeString()}`,
        location: "Taif HQ",
        sealed_by: "@CEO",
        timestamp: new Date().toISOString()
      }, { headers: { 'Content-Type': 'application/json' } });

      // التحقق من أن السيرفر أكد الحفظ فعلياً
      if (response.data && response.data.success) {
        message.success(i18n.language === 'ar' ? "تم التدشين وحفظ البيانات في القاعدة بنجاح ✅" : "Line Fully Launched!");
        await fetchProductionLines(); // تحديث القائمة فوراً
      } else {
        message.error(i18n.language === 'ar' ? `عائق في القاعدة: ${response.data.message || 'لم يتم الحفظ'}` : "Database rejection.");
      }
    } catch (err) { 
      message.error(i18n.language === 'ar' ? "عائق تقني في جسر البيانات السحابي" : "Network failure."); 
    } finally { 
      hide(); 
    }
  };

  const handleDeleteLine = async (e, lineId) => {
    e.stopPropagation();
    Modal.confirm({
      title: i18n.language === 'ar' ? 'تأكيد التطهير الصناعي' : 'Confirm Purge',
      content: i18n.language === 'ar' ? 'سعادة الرئيس، هل تؤكد حذف هذا الخط نهائياً من رادار الإنتاج؟' : 'CEO, confirm purging this line?',
      okText: i18n.language === 'ar' ? 'تطهير' : 'Purge',
      okType: 'danger',
      centered: true,
      getContainer: false, // منع البياض
      onOk: async () => {
        try {
          const res = await axios.post(API_URL, { action: 'delete_production_line', line_id: lineId });
          if (res.data.success) { 
            message.success(i18n.language === 'ar' ? "تم التطهير بنجاح ✅" : "Purged."); 
            fetchProductionLines(); 
          }
        } catch (err) { message.error("Fail."); }
      }
    });
  };

  const handleStatusChange = async (lineId, stageId, newStatus) => {
    setIsApproving(stageId);
    const hide = message.loading(i18n.language === 'ar' ? "جاري تطبيق الختم السيادي..." : "Applying Seal...", 0);
    try {
      const res = await axios.post(API_URL, { 
        action: 'update_stage_status', 
        line_id: lineId, 
        stage_id: stageId, 
        status: newStatus, 
        sealed_by: "@CEO" 
      });
      if (res.data && !res.data.error) { 
        message.success(i18n.language === 'ar' ? "تم التعميد بختم @CEO" : "Certified."); 
        fetchLineStages(lineId); 
        fetchProductionLines(); 
      }
    } catch (err) { message.error("Sync failure."); } finally { setIsApproving(null); hide(); }
  };

  // --- [ واجهة العرض: رادار الإنتاج الرئيسي ] ---
  if (!selectedLine) {
    return (
      <div className={`p-4 md:p-6 max-w-7xl mx-auto font-sans text-white bg-[#050B14] min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="absolute top-0 right-0 w-[40%] h-[40%] bg-[#F57C00]/5 rounded-full blur-[100px] pointer-events-none"></div>

        <header className="flex flex-col md:flex-row justify-between items-center mb-10 bg-[#0A192F]/60 backdrop-blur-2xl p-6 rounded-[2.5rem] border border-white/5 relative shadow-2xl scale-95 origin-top">
          <div className="flex items-center gap-5">
            <div className="p-4 bg-gradient-to-br from-[#F57C00] to-orange-900 rounded-2xl shadow-xl shadow-orange-500/20">
                <Factory className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-black italic tracking-tighter uppercase leading-none">
                {i18n.language === 'ar' ? 'رادار الإنتاج' : 'PRODUCTION RADAR'} <span className="text-[#F57C00]">SOVEREIGN</span>
              </h1>
              <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.4em] mt-2 italic">HUGER Taif Industrial HQ • 2026</p>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4 md:mt-0">
             <button 
                id="btn_launch_production_line"
                name="launch_action"
                onClick={handleCreateNewLine} 
                className="bg-[#F57C00] text-white px-8 py-3.5 rounded-2xl font-black text-[10px] flex items-center gap-2 hover:bg-[#FF9800] transition-all shadow-xl shadow-orange-500/20 active:scale-95 border-none cursor-pointer uppercase italic"
             >
              <Plus size={16}/> {i18n.language === 'ar' ? 'تدشين خط جديد' : 'Launch New Line'}
            </button>
          </div>
        </header>

        {loading && productionLines.length === 0 ? (
          <div className="py-32 text-center flex flex-col items-center gap-4">
            <Loader2 size={45} className="animate-spin text-[#F57C00]" />
            <p className="text-slate-600 font-black uppercase tracking-[0.5em] italic text-[10px]">Scanning Manufacturing Lanes...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 scale-95 origin-top">
            {productionLines.filter(l => l.name.toLowerCase().includes(searchTerm.toLowerCase())).map(line => (
              <div 
                key={line.id} 
                onClick={() => setSelectedLine(line)} 
                className="group bg-[#0A192F]/40 border border-white/5 p-8 rounded-[3rem] hover:border-[#F57C00]/40 transition-all cursor-pointer relative overflow-hidden shadow-2xl backdrop-blur-sm"
              >
                <div className="flex justify-between items-start mb-6 relative z-10">
                  <div className="bg-black/60 px-3 py-1 rounded-lg border border-white/5 text-[9px] font-black text-white italic uppercase tracking-widest flex items-center gap-2">
                    <Gauge size={14} className="text-[#F57C00]"/> {line.location}
                  </div>
                  <button 
                    onClick={(e) => handleDeleteLine(e, line.id)} 
                    className="p-2 text-slate-700 hover:text-red-500 transition-colors border-none bg-transparent cursor-pointer"
                  >
                    <Trash2 size={18}/>
                  </button>
                </div>
                <h3 className="text-xl font-black text-white mb-6 tracking-tighter h-12 overflow-hidden leading-tight italic">{line.name}</h3>
                <div className="space-y-4 bg-black/40 p-5 rounded-2xl border border-white/5">
                  <div className="flex justify-between text-[9px] font-black uppercase tracking-widest mb-1">
                    <span className="text-slate-500">Production Efficiency</span>
                    <span className="text-emerald-400">{line.progress}%</span>
                  </div>
                  {/* تحديث Progress لـ AntD الجديد */}
                  <Progress 
                    percent={line.progress} 
                    showInfo={false} 
                    strokeColor="#F57C00" 
                    trailColor="rgba(255,255,255,0.03)" 
                    size={{ strokeWidth: 4 }} 
                  />
                </div>
                <div className="mt-8 flex justify-between items-center relative z-10">
                  <div className="flex gap-4 opacity-20 group-hover:opacity-100 transition-all">
                    <Cpu size={16} className="text-[#F57C00]"/><Workflow size={16}/><HardDrive size={16}/>
                  </div>
                  <span className="text-[#F57C00] text-[9px] font-black uppercase italic tracking-widest group-hover:translate-x-[-8px] transition-transform flex items-center gap-2">View Route <ArrowLeft size={12} className="rotate-180"/></span>
                </div>
              </div>
            ))}
            {productionLines.length === 0 && !loading && (
              <div className="col-span-full py-24 text-center">
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<span className="text-slate-500 font-black italic uppercase tracking-widest text-[10px]">{i18n.language === 'ar' ? "لا توجد خطوط إنتاج نشطة حالياً بختم @CEO" : "No active strategic lines"}</span>} 
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- [ واجهة العرض: تفاصيل خط الإنتاج ] ---
  return (
    <div className={`p-4 md:p-6 max-w-6xl mx-auto font-sans text-white bg-[#050B14] min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <header className="flex justify-between items-center mb-8 bg-[#0A192F]/60 backdrop-blur-xl p-4 rounded-2xl border border-white/5 shadow-xl scale-95 origin-top">
        <button 
          onClick={() => setSelectedLine(null)} 
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-all font-black text-[10px] uppercase italic bg-transparent border-none cursor-pointer group"
        >
          <ArrowLeft size={18} className={`${i18n.language === 'en' ? 'rotate-180' : ''} group-hover:-translate-x-1 transition-transform`} />
          {i18n.language === 'ar' ? 'العودة لرادار الإنتاج' : 'Back to Radar'}
        </button>
        <span className="text-[8px] font-black text-[#F57C00] uppercase tracking-[0.5em] italic">COMMAND ACTIVE @CEO • 2026</span>
      </header>

      <div className="bg-gradient-to-br from-[#0A192F] to-[#050B14] p-10 rounded-[3.5rem] border-2 border-[#F57C00]/20 mb-10 text-center relative overflow-hidden scale-95 origin-top shadow-[0_0_50px_rgba(245,124,0,0.1)]">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5"></div>
        <Tag className="mb-4 font-black uppercase tracking-widest px-6 py-1 rounded-full bg-[#F57C00]/10 border-none text-[#F57C00] text-[8px] italic">Strategic Manufacturing Route</Tag>
        <h2 className="text-3xl md:text-5xl font-black italic text-white mb-4 tracking-tighter leading-none uppercase">{selectedLine.name}</h2>
        <div className="flex items-center justify-center gap-6 text-slate-500 font-black text-[9px] uppercase tracking-widest opacity-60">
          <span className="flex items-center gap-2"><Activity size={14} className="text-[#F57C00]"/> Production HQ</span>
          <span className="flex items-center gap-2"><Layers size={14} className="text-blue-500"/> Multi-Phased Assembly</span>
        </div>
      </div>

      {loadingStages ? (
        <div className="p-32 text-center scale-90"><Spin size="large" /></div>
      ) : (
        <div className="space-y-5 relative z-10 scale-95 origin-top pb-12">
          {lineStages.length > 0 ? lineStages.map((stage) => (
            <div key={stage.id} className={`p-8 rounded-[2.5rem] border flex flex-col md:flex-row justify-between items-center transition-all duration-500 ${stage.status === 'approved' ? 'border-emerald-500/20 bg-emerald-500/5 shadow-lg shadow-emerald-500/5' : 'border-white/5 bg-[#0A192F]/40 hover:border-white/10'}`}>
              <div className="flex items-center gap-8">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl transition-all ${stage.status === 'approved' ? 'bg-emerald-600 scale-110' : 'bg-slate-800'}`}>
                  {stage.status === 'approved' ? <ShieldCheck size={28} className="text-white" /> : <Timer size={28} className="text-slate-500 animate-pulse" />}
                </div>
                <div>
                  <h4 className="text-xl font-black text-white italic tracking-tight mb-2 uppercase">{stage.name}</h4>
                  <div className="flex items-center gap-3">
                    <span className={`text-[8px] font-black uppercase px-4 py-1 rounded-lg border ${stage.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/20 animate-pulse'}`}>
                      {stage.status === 'approved' ? 'SEALED & VERIFIED' : 'PENDING APPROVAL'}
                    </span>
                    {stage.status === 'approved' && <Badge status="success" />}
                  </div>
                </div>
              </div>
              <div className="mt-6 md:mt-0">
                {stage.status !== 'approved' ? (
                  <button 
                    onClick={() => handleStatusChange(selectedLine.id, stage.id, 'approved')} 
                    disabled={isApproving === stage.id} 
                    className="bg-white text-black px-10 py-4 rounded-xl font-black text-[11px] hover:bg-[#F57C00] hover:text-white transition-all shadow-2xl active:scale-95 flex items-center gap-3 border-none cursor-pointer uppercase italic"
                  >
                    {isApproving === stage.id ? <Loader2 size={16} className="animate-spin"/> : <><Fingerprint size={20}/> {i18n.language === 'ar' ? 'منح التعميد الصناعي' : 'Grant Industry Seal'}</>}
                  </button>
                ) : (
                  <div className="flex flex-col items-end opacity-90">
                    <span className="text-[9px] text-emerald-500 font-black italic uppercase tracking-[0.2em] flex items-center gap-2"><ShieldCheck size={18}/> Sealed @CEO</span>
                    <div className="text-[8px] text-slate-500 font-bold mt-1.5 uppercase italic">DELEGATED SYNC: {stage.updated_at || '2026.04.27'}</div>
                  </div>
                )}
              </div>
            </div>
          )) : <div className="py-24 text-center"><Empty description={<span className="text-slate-600 font-black italic uppercase tracking-widest text-[10px]">No stages registered for this route</span>} /></div>}
        </div>
      )}

      <footer className="mt-10 py-10 border-t border-white/5 text-center opacity-20">
        <p className="text-[8px] font-black uppercase tracking-[1.5em] italic">HUGER INDUSTRIAL EMPIRE • v12.8.5 • 2026 • AUTHORIZED BY @CEO</p>
      </footer>

      {/* حماية المودالات من البياض */}
      <style>{`
        .ant-modal-mask { background-color: rgba(0,0,0,0.85) !important; backdrop-filter: blur(8px); }
        .ant-modal-content { background: #050B14 !important; border-radius: 2.5rem !important; border: 1px solid rgba(245,124,0,0.3) !important; }
        .ant-progress-inner { background: rgba(255,255,255,0.03) !important; }
      `}</style>
    </div>
  );
};

export default ProductionTracker;